import { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet.markercluster';
import { useT } from '@/store';
import { useAllQuarries, useSites } from '@/hooks/useDirectory';
import { useDwellStatus } from '@/hooks/useTenant';
import { api } from '@/services/api';
import { useSSESubscription } from '@/services/sse';
import { useQuery } from '@tanstack/react-query';
import { qk } from '@/lib/queryKeys';
import { useTenantId } from '@/hooks/useTenant';
import { escapeHtml, minutesSince, STALE_FIX_MINUTES } from '@/lib/format';
import { driverPopupHtml } from '@/lib/driverPopup';
import { basemapLayer } from '@/lib/basemap';
import { Panel } from '@/components/ui/Panel';

const LITHUANIA_CENTER = [55.32, 23.9];

const QUARRY_GEOFENCE_STYLE = {
  color: '#22D3EE',
  weight: 1.5,
  opacity: 0.4,
  dashArray: '4 4',
  fillColor: '#22D3EE',
  fillOpacity: 0.06,
};

const SITE_GEOFENCE_STYLE = {
  color: '#14B8A6',
  weight: 1.5,
  opacity: 0.4,
  dashArray: '4 4',
  fillColor: '#14B8A6',
  fillOpacity: 0.06,
};

function pinIcon({ color, glyph, stale = false, dwell = false }) {
  return L.divIcon({
    className: '',
    html: `<div class="pin ${stale ? 'pin-stale' : ''} ${dwell ? 'pin-dwell' : ''}" style="background:${color}"><span>${glyph}</span></div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 26],
    popupAnchor: [0, -24],
  });
}

function quarryIcon(category) {
  return pinIcon({
    color: category === 'competitor' ? '#8A90A0' : '#14B8A6',
    glyph: '⛏',
  });
}

function useDriverPositions() {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.drivers(tenantId, { positions: true }),
    queryFn: () => api(`/api/tenants/${tenantId}/drivers`),
    enabled: Boolean(tenantId),
    refetchInterval: 120_000,
    placeholderData: [],
  });
}

export function LiveMap({ fill = false }) {
  const t = useT();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const tileRef = useRef(null);
  const clusterRef = useRef(null);
  const quarryMarkersRef = useRef({});
  const driverMarkersRef = useRef({});
  const geofenceLayerRef = useRef(null);
  const quarryGeofencesRef = useRef({});
  const siteGeofencesRef = useRef({});
  const fittedRef = useRef(false);
  const [dark, setDark] = useState(true);

  const { data: quarries = [] } = useAllQuarries();
  const { data: sites = [] } = useSites();
  const livePositionsRef = useRef({});
  const { data: drivers = [] } = useDriverPositions();
  const { data: dwell } = useDwellStatus();
  const dwellByDriver = useMemo(() => dwell?.byDriver ?? {}, [dwell]);

  const locatedDrivers = useMemo(
    () => drivers.filter((d) => d.last_known_lat != null && d.last_known_lng != null),
    [drivers],
  );

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return undefined;
    const map = L.map(containerRef.current, { zoomControl: true, attributionControl: true }).setView(
      LITHUANIA_CENTER,
      7,
    );
    tileRef.current = basemapLayer({ dark: true }).addTo(map);
    clusterRef.current = L.markerClusterGroup({ showCoverageOnHover: false, maxClusterRadius: 45 }).addTo(map);
    geofenceLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      tileRef.current = null;
      clusterRef.current = null;
      quarryMarkersRef.current = {};
      driverMarkersRef.current = {};
      geofenceLayerRef.current = null;
      quarryGeofencesRef.current = {};
      siteGeofencesRef.current = {};
      fittedRef.current = false;
    };
  }, []);

  // Day/night. The raster layer this replaced could swap its URL in place;
  // a vector one cannot, because the flavor drives paint rules rather than a
  // URL template (protomaps-leaflet#176). So the layer is replaced instead —
  // remove, build, add. Everything else on the map lives in other layer
  // groups (clusters, geofences) and is untouched by this, which is why the
  // markers do not flicker when a dispatcher hits the toggle.
  //
  // appliedDarkRef exists because this effect also runs on mount, where the
  // init effect above has just added a layer at the same flavor. Without the
  // guard, every load tore down a layer mid-fetch and built an identical one
  // — invisible with raster tiles, wasteful with an archive being read over
  // HTTP ranges.
  const appliedDarkRef = useRef(true);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !tileRef.current) return;
    if (appliedDarkRef.current === dark) return;
    appliedDarkRef.current = dark;
    map.removeLayer(tileRef.current);
    tileRef.current = basemapLayer({ dark }).addTo(map);
    // Keep the basemap under the markers. addTo() appends, and Leaflet's
    // default pane ordering would otherwise leave a freshly added tile
    // layer painting over the cluster group.
    tileRef.current.bringToBack();
  }, [dark]);

  useEffect(() => {
    const cluster = clusterRef.current;
    if (!cluster) return;
    quarries.forEach((q) => {
      if (q.lat == null || q.lng == null || quarryMarkersRef.current[q.quarry_id]) return;
      // escapeHtml() on both: bindPopup sets content via innerHTML, and a
      // quarry name/materials list is a database value, not a literal —
      // see escapeHtml()'s own docstring for why "admin-entered" is not
      // an exemption.
      const marker = L.marker([q.lat, q.lng], { icon: quarryIcon(q.category) }).bindPopup(
        `<strong>${escapeHtml(q.name)}</strong><br>${escapeHtml(q.materials_available)}`,
      );
      quarryMarkersRef.current[q.quarry_id] = marker;
      cluster.addLayer(marker);
    });
  }, [quarries]);

  useEffect(() => {
    const geofenceLayer = geofenceLayerRef.current;
    if (!geofenceLayer) return;
    quarries.forEach((q) => {
      if (q.lat == null || q.lng == null || quarryGeofencesRef.current[q.quarry_id]) return;
      const radius = q.geofence_radius_m ?? 200;
      const circle = L.circle([q.lat, q.lng], { radius, ...QUARRY_GEOFENCE_STYLE });
      if (q.name) circle.bindTooltip(escapeHtml(q.name), { direction: 'top', sticky: true });
      quarryGeofencesRef.current[q.quarry_id] = circle;
      geofenceLayer.addLayer(circle);
    });
  }, [quarries]);

  useEffect(() => {
    const geofenceLayer = geofenceLayerRef.current;
    if (!geofenceLayer) return;
    sites.forEach((s) => {
      if (s.lat == null || s.lng == null || siteGeofencesRef.current[s.site_id]) return;
      const radius = s.geofence_radius_m ?? 150;
      const circle = L.circle([s.lat, s.lng], { radius, ...SITE_GEOFENCE_STYLE });
      const label = s.site_name ?? s.client_name;
      if (label) circle.bindTooltip(escapeHtml(label), { direction: 'top', sticky: true });
      siteGeofencesRef.current[s.site_id] = circle;
      geofenceLayer.addLayer(circle);
    });
  }, [sites]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const seen = new Set();
    locatedDrivers.forEach((row) => {
      const live = livePositionsRef.current[row.driver_id];
      const d = live
        ? { ...row, last_known_lat: live.lat, last_known_lng: live.lng, last_ping_at: live.recorded_at }
        : row;
      seen.add(d.driver_id);
      const stale = (minutesSince(d.last_ping_at) ?? Infinity) > STALE_FIX_MINUTES;
      // Only a stop that is still a real wait rings the pin. An 'unclosed'
      // record is one nobody ever closed — the truck left at some unknown
      // earlier time, and ringing its marker would put a truck on the map
      // at a quarry it drove away from days ago.
      const dwelling = dwellByDriver[d.driver_id]?.state === 'exceeding';
      const icon = pinIcon({ color: stale ? '#8C99AC' : '#D08A4E', glyph: '🚛', stale, dwell: dwelling });
      const popup = driverPopupHtml(d, t);

      const existing = driverMarkersRef.current[d.driver_id];
      if (existing) {
        existing.setLatLng([d.last_known_lat, d.last_known_lng]);
        existing.setIcon(icon);
        existing.setPopupContent(popup);
      } else {
        driverMarkersRef.current[d.driver_id] = L.marker([d.last_known_lat, d.last_known_lng], { icon })
          .bindPopup(popup)
          .addTo(map);
      }
    });

    Object.entries(driverMarkersRef.current).forEach(([driverId, marker]) => {
      if (seen.has(driverId)) return;
      marker.remove();
      delete driverMarkersRef.current[driverId];
    });

    if (!fittedRef.current) {
      const points = [
        ...locatedDrivers.map((d) => [d.last_known_lat, d.last_known_lng]),
        ...quarries.filter((q) => q.lat != null).map((q) => [q.lat, q.lng]),
        ...sites.filter((s) => s.lat != null).map((s) => [s.lat, s.lng]),
      ];
      if (points.length) {
        map.fitBounds(L.latLngBounds(points).pad(0.15));
        fittedRef.current = true;
      }
    }
  }, [locatedDrivers, quarries, sites, dwellByDriver, t]);

  useSSESubscription('driver_moved', (payload) => {
    const { driver_id: driverId, lat, lng, recorded_at: recordedAt } = payload;
    if (driverId == null || lat == null || lng == null) return;

    livePositionsRef.current[driverId] = { lat, lng, recorded_at: recordedAt };
    const marker = driverMarkersRef.current[driverId];
    if (marker) marker.setLatLng([lat, lng]);
  });

  if (fill) {
    return <div ref={containerRef} className="h-full w-full" />;
  }

  return (
    <Panel
      title={t('dispatcher.spatialView')}
      actions={
        <button type="button" className="btn" onClick={() => setDark((v) => !v)}>
          {t('dispatcher.toggleDayNight')}
        </button>
      }
      bodyClassName="space-y-2"
    >
      <div ref={containerRef} className="h-[65vh] min-h-[420px] w-full overflow-hidden rounded-[12px] border border-line" />
      <div className="flex flex-wrap gap-3 text-[10.5px] text-dim">
        <Legend color="#14B8A6" glyph="⛏" label={t('dispatcher.legendOwnQuarry')} />
        <Legend color="#8A90A0" glyph="⛏" label={t('dispatcher.legendCompetitorQuarry')} />
        <Legend color="#D08A4E" glyph="🚛" label={t('dispatcher.legendTruck')} />
      </div>
    </Panel>
  );
}

function Legend({ color, glyph, label }) {
  return (
    <span className="flex items-center gap-1.5">
      <span
        className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-[8px]"
        style={{ background: color }}
      >
        {glyph}
      </span>
      {label}
    </span>
  );
}
